import { useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import { useQuery } from '@tanstack/react-query';

import { lookupAirports } from '../api/endpoints';
import type { AirportHit } from '../api/types';

type Props = {
  value?: string;
  onChange?: (code: string, label?: string) => void;
  // SHAR-600/603 — the charter quote form needs the whole airport, not just its
  // code: the city and country to display, and the coordinates to estimate the
  // flight duration. Optional, so every existing caller is unaffected.
  onSelectAirport?: (airport: AirportHit | null) => void;
};

// SHAR-12 — homebase airport typeahead. Search by name/code/city.
export function AirportSelect({ value, onChange, onSelectAirport }: Props) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(t);
  }, [q]);

  const lookup = useQuery({
    queryKey: ['airports', debounced],
    queryFn: () => lookupAirports(debounced),
    enabled: debounced.trim().length >= 2,
  });

  const options = useMemo(() => {
    const rows = lookup.data || [];
    return rows.map((a) => ({
      value: a.icaoCode,
      label: `${a.icaoCode} — ${a.name}${a.city ? `, ${a.city}` : ''} (${a.country})`,
    }));
  }, [lookup.data]);

  return (
    <Select
      showSearch
      placeholder="Start typing — name, code or city"
      value={value}
      filterOption={false}
      onSearch={setQ}
      onChange={(v, opt: any) => {
        onChange?.(v, opt?.label);
        onSelectAirport?.((lookup.data || []).find((a) => a.icaoCode === v) ?? null);
      }}
      notFoundContent={lookup.isFetching ? <Spin size="small" /> : null}
      options={options}
      // SHAR-352 — the dropdown shows full airport details, but once selected only
      // the short ICAO code (the option value) fills the field, so the modal layout
      // doesn't break on a long multi-line label.
      optionLabelProp="value"
      style={{ width: '100%' }}
    />
  );
}
